import * as React from 'react';

import type { CampaignStateDto } from '@/api/campaign-client';
import type { DashboardCampaignRowDto } from '@/api/operational-view-client';

// =============================================================================
// campaign-registry-format.tsx — Habillage PARTAGÉ des registres de campagnes (A-02 dashboard, A-12 liste).
// =============================================================================
// Extrait d'admin-dashboard-screen.tsx (maquettes John 2026-09-16) pour que le tableau de bord et la page
// « Campagnes » rendent le registre à l'identique (polices, tailles, libellés). Fonctions et composants PURS :
// aucune règle métier (criticité, échéances, jours restants = autorité API), l'UI LIT et habille.
//
// WCAG 2.1 AA : statut / criticité / retard toujours portés par un LIBELLÉ TEXTE (la pastille ou la couleur
// n'est qu'un renfort) ; en-têtes triables boutonnés avec aria-sort.

/** Signe moins typographique (J−1, jamais « J-1 » ni « J--7 »). */
export const MINUS = '−';

/** Pluriel français simple (0 et 1 au singulier). */
export function plural(
  count: number,
  singular: string,
  pluralForm?: string,
): string {
  return `${String(count)} ${count > 1 ? (pluralForm ?? `${singular}s`) : singular}`;
}

/** Date courte FR « 15 sept. » (année ajoutée hors année courante) ; « — » si absente/invalide. */
export function formatShortDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

type StatusDotKind = 'open' | 'progress' | 'draft' | 'closed' | 'cancelled';

/** État campagne → libellé du registre + pastille (le texte porte l'information, la pastille renforce). */
export const REGISTRY_STATE: Record<
  CampaignStateDto,
  { readonly label: string; readonly dot: StatusDotKind }
> = {
  Brouillon: { label: 'Brouillon', dot: 'draft' },
  Planifiee: { label: 'Planifiée', dot: 'draft' },
  OuverteC1: { label: 'Ouverte · cycle 1', dot: 'open' },
  AnalyseC1: { label: 'Analyse · cycle 1', dot: 'progress' },
  OuverteC2: { label: 'Ouverte · cycle 2', dot: 'open' },
  AnalyseC2: { label: 'Analyse · cycle 2', dot: 'progress' },
  Negociation: { label: 'Négociation', dot: 'progress' },
  Publiee: { label: 'Publiée', dot: 'closed' },
  Abandonnee: { label: 'Abandonnée', dot: 'cancelled' },
};

const DOT_CLASS: Record<StatusDotKind, string> = {
  open: 'bg-success',
  progress: 'bg-primary',
  draft: 'border border-muted-foreground bg-transparent',
  closed: 'bg-muted-foreground',
  cancelled: 'bg-foreground',
};

/** Statut de campagne : pastille décorative + libellé texte. */
export function StatusLabel({
  state,
}: {
  readonly state: CampaignStateDto;
}): React.JSX.Element {
  const { label, dot } = REGISTRY_STATE[state];
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span
        aria-hidden="true"
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT_CLASS[dot]}`}
      />
      {label}
    </span>
  );
}

/**
 * Une échéance de cycle : « C1 15 sept. J−1 » ou « C1 13 sept. +1 j » (retard en rouge). Un cycle CLÔTURÉ ou
 * VERROUILLÉ n'a plus d'échéance à tenir : date seule + « clôturé », jamais de compte à rebours ni de retard.
 */
export function CycleDeadline({
  cycle,
  showCountdown,
}: {
  readonly cycle: DashboardCampaignRowDto['cycles'][number];
  readonly showCountdown: boolean;
}): React.JSX.Element {
  const days = cycle.daysRemaining;
  const isClosed = cycle.state === 'Cloturee' || cycle.state === 'Verrouillee';
  return (
    <span className="whitespace-nowrap">
      C{cycle.cycleNumber} {formatShortDate(cycle.closeAt)}
      {isClosed ? (
        <span className="text-muted-foreground"> clôturé</span>
      ) : showCountdown && days !== null ? (
        days < 0 ? (
          <span className="text-dash-late">
            {' '}
            +{-days} j<span className="sr-only"> de retard</span>
          </span>
        ) : (
          <span className="text-muted-foreground">
            {' '}
            J{MINUS}
            {days}
          </span>
        )
      ) : null}
    </span>
  );
}

/** Criticité R/O (DBU l.1073) en badge texte ; V (nominal) n'affiche rien. */
export function CriticalityBadge({
  criticality,
}: {
  readonly criticality: DashboardCampaignRowDto['criticality'];
}): React.JSX.Element | null {
  if (criticality === 'V') {
    return null;
  }
  const isCritical = criticality === 'R';
  return (
    <span
      className={`rounded border px-1.5 py-px text-xs font-normal ${
        isCritical
          ? 'border-dash-critical-fg/30 bg-dash-critical-bg text-dash-critical-fg'
          : 'border-border bg-muted text-muted-foreground'
      }`}
    >
      {isCritical ? 'Critique' : 'Attention'}
    </span>
  );
}

export type SortDir = 'asc' | 'desc';

/** État de tri d'un registre (colonne active + sens). */
export interface SortState<K extends string> {
  readonly key: K;
  readonly dir: SortDir;
}

/** En-tête de colonne triable : bouton + aria-sort ; indicateur visible sur la colonne active seulement. */
export function SortableHeader<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  readonly label: string;
  readonly sortKey: K;
  readonly sort: SortState<K>;
  readonly onSort: (key: K) => void;
  readonly className?: string;
}): React.JSX.Element {
  const active = sort.key === sortKey;
  const ariaSort = active
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`py-3 pr-4 ${className ?? 'pl-4'}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wider hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {label}
        {active ? (
          <span aria-hidden="true">{sort.dir === 'asc' ? '▴' : '▾'}</span>
        ) : null}
      </button>
    </th>
  );
}
